import React, { useState } from "react";
import { platformAPI } from "../services/api";
import Modal from "./Modal";
import SelectBox from "./SelectBox";
import { plural } from "../utils/grouping";

const API_ORIGIN = process.env.REACT_APP_API_URL || "http://localhost:3010/api";

const errorText = (err, fallback) =>
  err.response?.data?.error || err.response?.data?.message || fallback;

const primaryBtn =
  "px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold text-sm disabled:opacity-50";
const secondaryBtn = "px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-sm";
const fieldClass = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm";

function AddFlavoursDialog({ templateId, group, onClose, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const labels = text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await platformAPI.addTemplateGroupMembers(templateId, group.id, labels);
      setResult({ added: data.created.length, skipped: data.skipped.length });
      setText("");
      onDone();
    } catch (err) {
      setError(errorText(err, "Failed to add"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Add flavours to ${group.name}`} onClose={onClose}>
      <p className="text-sm text-gray-500 mb-2">
        One per line (a flavour or a colour). Each becomes a product named “{group.name} …” with the
        range's details. Labels already in the range are skipped.
      </p>
      <textarea
        rows={8}
        className={fieldClass}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={"Watermelon Ice\nBlue Razz\nMango"}
        autoFocus
      />
      <p className="text-xs text-gray-500 mt-1">{plural(labels.length, "label")} to add (max 100)</p>
      {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
      {result && (
        <p className="text-sm text-green-700 mt-2">
          Added {result.added}, skipped {result.skipped}.
        </p>
      )}
      <div className="flex justify-end gap-3 mt-4">
        <button onClick={onClose} className={secondaryBtn}>
          {result ? "Close" : "Cancel"}
        </button>
        <button onClick={submit} disabled={busy || labels.length === 0 || labels.length > 100} className={primaryBtn}>
          {busy ? "Adding…" : `Add ${plural(labels.length, "product")}`}
        </button>
      </div>
    </Modal>
  );
}

const APPLY_FIELDS = [
  ["price", "Price"],
  ["image", "Picture"],
  ["brand", "Brand"],
  ["category", "Category"],
  ["subcategory", "Subcategory"],
  ["details", "Details"],
];

function ApplyDialog({ templateId, group, count, onClose, onDone }) {
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState("");

  const toggle = (key) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const submit = async () => {
    setBusy(true);
    setError("");
    setResult("");
    try {
      const { data } = await platformAPI.applyTemplateGroup(templateId, group.id, Array.from(selected));
      setResult(`Updated ${plural(data.updated, "product")}`);
      onDone();
    } catch (err) {
      setError(errorText(err, "Failed to apply"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Apply to members of ${group.name}`} onClose={onClose}>
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800 mb-4">
        This overwrites the fields you tick on all {plural(count, "product")} of this range with the
        range's values. Their names, labels and every other field are not touched.
      </div>
      <div className="space-y-2">
        {APPLY_FIELDS.map(([key, label]) => (
          <label key={key} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={selected.has(key)} onChange={() => toggle(key)} />
            {label}
          </label>
        ))}
      </div>
      {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
      {result && <p className="text-sm text-green-700 mt-3">{result}</p>}
      <div className="flex justify-end gap-3 mt-4">
        <button onClick={onClose} className={secondaryBtn}>
          {result ? "Close" : "Cancel"}
        </button>
        <button onClick={submit} disabled={busy || selected.size === 0 || count === 0} className={primaryBtn}>
          {busy ? "Applying…" : `Overwrite on ${plural(count, "product")}`}
        </button>
      </div>
    </Modal>
  );
}

function ConfirmDeleteDialog({ templateId, group, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await platformAPI.deleteTemplateGroup(templateId, group.id);
      onDone();
    } catch (err) {
      setError(errorText(err, "Failed to delete"));
      setBusy(false);
    }
  };

  return (
    <Modal title="Delete range?" onClose={onClose}>
      <p className="text-sm text-gray-700">
        “{group.name}” will be deleted. Its {plural(group.member_count || 0, "product")} are kept and simply
        become ordinary products.
      </p>
      {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
      <div className="flex justify-end gap-3 mt-4">
        <button onClick={onClose} className={secondaryBtn}>Cancel</button>
        <button
          onClick={submit}
          disabled={busy}
          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold text-sm disabled:opacity-50"
        >
          {busy ? "Deleting…" : "Delete range"}
        </button>
      </div>
    </Modal>
  );
}

/** Pick a range and type a label for a loose product. */
export function AddToRangeDialog({ templateId, item, groups, onClose, onDone }) {
  const [groupId, setGroupId] = useState(groups[0]?.id || "");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await platformAPI.patchTemplateItem(templateId, item.id, {
        variant_group_id: groupId,
        variant_label: label.trim(),
      });
      onDone();
    } catch (err) {
      setError(errorText(err, "Failed to add to range"));
      setBusy(false);
    }
  };

  return (
    <Modal title={`Add “${item.name}” to a range`} onClose={onClose}>
      <label className="block text-sm font-medium text-gray-700 mb-1">Range</label>
      <select className={`${fieldClass} mb-3`} value={groupId} onChange={(e) => setGroupId(e.target.value)}>
        {groups.map((g) => (
          <option key={g.id} value={g.id}>{g.name}</option>
        ))}
      </select>
      <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
      <input
        className={fieldClass}
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="e.g. Watermelon Ice"
        autoFocus
      />
      {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
      <div className="flex justify-end gap-3 mt-4">
        <button onClick={onClose} className={secondaryBtn}>Cancel</button>
        <button onClick={submit} disabled={busy || !groupId || !label.trim()} className={primaryBtn}>
          {busy ? "Saving…" : "Add to range"}
        </button>
      </div>
    </Modal>
  );
}

function MemberRow({ templateId, item, onEditProduct, onChanged, selecting, selected, onSelectItems }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.variant_label || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const saveLabel = async () => {
    const next = draft.trim();
    if (!next || next === item.variant_label) {
      setEditing(false);
      setDraft(item.variant_label || "");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await platformAPI.patchTemplateItem(templateId, item.id, { variant_label: next });
      setEditing(false);
      onChanged();
    } catch (err) {
      setError(errorText(err, "Failed to rename"));
    } finally {
      setBusy(false);
    }
  };

  const removeFromRange = async () => {
    setBusy(true);
    setError("");
    try {
      await platformAPI.patchTemplateItem(templateId, item.id, { variant_group_id: "" });
      onChanged();
    } catch (err) {
      setError(errorText(err, "Failed to remove from range"));
      setBusy(false);
    }
  };

  return (
    <div className="px-4 py-2 text-sm">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {selecting && (
          <SelectBox
            checked={selected.has(item.id)}
            onChange={(on) => onSelectItems([item.id], on)}
            label={`Select ${item.name}`}
          />
        )}
        {item.images?.[0] || item.image_url ? (
          <img
            src={`${API_ORIGIN}${item.images?.[0] || item.image_url}`}
            alt=""
            className="w-10 h-10 rounded-lg object-cover border shrink-0"
          />
        ) : (
          <span className="w-10 h-10 shrink-0" />
        )}
        <div className="min-w-0 flex-1 basis-48">
          {editing ? (
            <div className="flex items-center gap-2">
              <input
                className="px-2 py-1 border border-gray-300 rounded text-sm w-40"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveLabel();
                  if (e.key === "Escape") {
                    setEditing(false);
                    setDraft(item.variant_label || "");
                  }
                }}
                autoFocus
              />
              <button onClick={saveLabel} disabled={busy} className="text-primary-700 hover:underline">
                Save
              </button>
            </div>
          ) : (
            <button
              onClick={() => setEditing(true)}
              title="Rename label"
              className="font-medium text-gray-800 hover:underline text-start"
            >
              {item.variant_label}
            </button>
          )}
          <p className="text-xs text-gray-400 truncate">
            {item.name}
            {item.price != null && item.price !== "" ? ` · ${item.price}` : ""}
          </p>
        </div>
        <div className="space-x-3 shrink-0">
          <button onClick={() => onEditProduct(item)} className="text-primary-700 hover:underline">
            Edit product
          </button>
          <button onClick={removeFromRange} disabled={busy} className="text-red-600 hover:underline">
            Remove from range
          </button>
        </div>
      </div>
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}

/**
 * One range: a compact header row that expands to its products. Collapsed it
 * is a single line (name, count), so a template with many ranges stays
 * scannable; the range-level actions only appear once it is open.
 */
export function RangeCard({
  templateId,
  group,
  members,
  onEditProduct,
  onChanged,
  onEditRange,
  expanded,
  onToggle,
  selecting,
  selected,
  onSelectItems,
}) {
  const [dialog, setDialog] = useState(null); // "flavours" | "apply" | "delete"
  const image = group.images?.[0] || group.image_url;
  const meta = [group.brand, group.subcategory].filter(Boolean).join(" · ");

  const memberIds = members.map((m) => m.id);
  const pickedCount = memberIds.filter((id) => selected.has(id)).length;

  return (
    <div className="bg-white rounded-xl shadow">
      <div className={`flex items-center gap-3 px-3 py-2 ${expanded ? "border-b border-gray-100" : ""}`}>
        {selecting && (
          <SelectBox
            checked={memberIds.length > 0 && pickedCount === memberIds.length}
            indeterminate={pickedCount > 0}
            onChange={(on) => onSelectItems(memberIds, on)}
            label={`Select every product of ${group.name}`}
          />
        )}
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          className="flex min-w-0 flex-1 items-center gap-3 text-start"
        >
          <span className={`text-gray-400 text-xs transition-transform ${expanded ? "rotate-90" : ""}`}>▶</span>
          {image && (
            <img src={`${API_ORIGIN}${image}`} alt="" className="w-10 h-10 rounded-lg object-cover border shrink-0" />
          )}
          <span className="min-w-0 flex-1">
            <span className="block font-semibold text-gray-900 truncate">{group.name}</span>
            {meta && <span className="block text-xs text-gray-500 truncate">{meta}</span>}
          </span>
          <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
            Range · {plural(members.length, "product")}
          </span>
        </button>
      </div>

      {expanded && (
        <>
          <div className="px-4 py-2 flex flex-wrap gap-x-4 gap-y-1 text-sm border-b border-gray-100 bg-gray-50">
            <button onClick={() => setDialog("flavours")} className="text-primary-700 hover:underline">
              Add flavours
            </button>
            <button onClick={onEditRange} className="text-primary-700 hover:underline">
              Edit range
            </button>
            <button onClick={() => setDialog("apply")} className="text-primary-700 hover:underline">
              Apply to members…
            </button>
            <button onClick={() => setDialog("delete")} className="text-red-600 hover:underline">
              Delete range
            </button>
          </div>

          <div className="divide-y divide-gray-100">
            {members.map((item) => (
              <MemberRow
                key={item.id}
                templateId={templateId}
                item={item}
                onEditProduct={onEditProduct}
                onChanged={onChanged}
                selecting={selecting}
                selected={selected}
                onSelectItems={onSelectItems}
              />
            ))}
            {members.length === 0 && (
              <p className="px-4 py-4 text-sm text-gray-400">
                No products yet — use “Add flavours” to create them.
              </p>
            )}
          </div>
        </>
      )}

      {dialog === "flavours" && (
        <AddFlavoursDialog templateId={templateId} group={group} onClose={() => setDialog(null)} onDone={onChanged} />
      )}
      {dialog === "apply" && (
        <ApplyDialog
          templateId={templateId}
          group={group}
          count={members.length}
          onClose={() => setDialog(null)}
          onDone={onChanged}
        />
      )}
      {dialog === "delete" && (
        <ConfirmDeleteDialog
          templateId={templateId}
          group={{ ...group, member_count: members.length }}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            onChanged();
          }}
        />
      )}
    </div>
  );
}
